import {
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { RouterModule } from '@angular/router'
import { TranslationService } from '../../../../services/translation.service'
import { AuthService } from '../../../../services/auth.service'
import { firstValueFrom } from 'rxjs'
import { MfaCodeBody, SessionResponse } from '../../../../types/auth'
import { DialogService } from '../../../../services/dialog.service'
import { ErrorService } from '../../../../services/error.service'
import { SpinnerService } from '../../../../services/spinner.service'
import { ApiError } from '../../../../types/apiError'
import { Dialog } from '@angular/cdk/dialog'

@Component({
  selector: 'app-mfa',
  imports: [
    MatFormFieldModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    RouterModule,
  ],
  templateUrl: './mfa.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './mfa.component.scss',
})
export class MfaComponent {
  translationService = inject(TranslationService)
  private authService = inject(AuthService)
  private errorService = inject(ErrorService)
  private spinnerService = inject(SpinnerService)
  private dialogService = inject(DialogService)
  dialog = inject(Dialog)

  @Input({ required: true }) userId?: string
  @Output() mfaVerified = new EventEmitter<SessionResponse>()

  mfaForm = new FormGroup({
    code: new FormControl<string>('', [Validators.required]),
  })

  /**
   * Submits request to verify user's mfa code
   */
  async submit() {
    try {
      if (!this.userId || this.mfaForm.invalid) {
        return
      }

      this.spinnerService.startSpinner()

      const mfaBody: MfaCodeBody = this.mfaForm.value as MfaCodeBody

      const sessionResponse = await firstValueFrom(
        this.authService.verifyMfaCode(this.userId, mfaBody.code),
      )
      this.spinnerService.stopSpinner()

      this.mfaVerified.emit(sessionResponse)
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  /**
   * Makes request to resend a user's mfa code to their email
   */
  async resendCode() {
    try {
      if (!this.userId) {
        return
      }
      this.spinnerService.startSpinner()
      await firstValueFrom(this.authService.resendMfaCode(this.userId))
      this.spinnerService.stopSpinner()
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }
}
